import { add, viewDir } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { abilIdx, ActionKind, hv, MoveKind, setHv } from "../../sim/components.ts";
import { addOverhealth, applyDamage, applyHeal, applyStatus, creditMitigation } from "../../sim/combat.ts";
import { action, climb, commonInput, consume, contactEnemies, knockUp, ready, secs, startMove, trigger, useUlt } from "../kitlib.ts";
import { fxk, type HeroModule } from "../module.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";

export const V = { combo: 0, chi: 1, guard: 2, meditate: 3 } as const;
export const FX = { Fist: fxk(HeroId.IronyFist, 0), Kick: fxk(HeroId.IronyFist, 1), Chi: fxk(HeroId.IronyFist, 2) } as const;

const KIND_KICK = MoveKind.Hero;
const EMPOWER_TICKS = 5 * 60;

const def: HeroDef = {
  id: HeroId.IronyFist,
  slug: "irony-fist",
  name: "Irony Fist",
  role: Role.Damage,
  color: "#f2c14e",
  blurb: "Close-range mystic boxer who parries pressure, heals midair, and chains kicks into flurries.",
  health: 300,
  armor: 0,
  shields: 0,
  height: 1.8,
  radius: 0.4,
  headRadius: 0.22,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 3100,
  abilities: {
    [Slot.Primary]: { name: "Dry Wit Combo", key: "LMB", description: "Lock onto nearby enemies with fast punches; empowered strikes hit faster and harder.", cooldown: 0 },
    [Slot.Secondary]: { name: "Dragon Rebuttal", key: "RMB", description: "Enter a brief parry stance, gaining protection, bonus health, and empowered punches.", cooldown: 15 },
    [Slot.Ability1]: { name: "Mountain Punchline", key: "Shift", description: "Launch forward 12 m with a kick that hits wounded enemies twice as hard.", cooldown: 10 },
    [Slot.Ability2]: { name: "Stillness Receipt", key: "E", description: "Meditate for 3 seconds, healing yourself and converting excess healing to bonus health.", cooldown: 12 },
    [Slot.Ultimate]: { name: "Living Punchline", key: "Q", description: "Flood with chi, gaining speed, range, damage, and faster guard recovery.", cooldown: 0 },
  },
  passives: [
    { name: "Wall-Retort Run", description: "Hold Jump into walls to run upward." },
    { name: "Chi Absorption", description: "Finishing blows leave you with a short burst of bonus health." },
  ],
};

function empowered(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  return hv(sim.c, eid, V.chi) > 0 || sim.c.Ult.active[eid]! > 0;
}

function punchDamage(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, target: number) {
  if (!empowered(sim, eid)) return 38;
  const ult = sim.c.Ult.active[eid]! > 0;
  return (ult ? 10.4 : 8) + sim.c.Health.maxHp[target]! * (ult ? 0.0403 : 0.031);
}

function reduceGuard(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, ticks: number) {
  const i = abilIdx(eid, Slot.Secondary);
  sim.c.Abil.cd[i] = Math.max(0, sim.c.Abil.cd[i]! - ticks);
  if (sim.c.Abil.cd[i] <= 0) sim.c.Abil.charges[i] = 1;
}

function strike(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, range: number, kick = false) {
  let hit = false;
  contactEnemies(sim, eid, range, (t) => {
    hit = true;
    const wounded = sim.c.Health.hp[t]! + sim.c.Health.armor[t]! + sim.c.Health.shield[t]! < sim.c.Health.maxHp[t]! * 0.5;
    const dmg = kick ? 35 * (wounded ? 2 : 1) : punchDamage(sim, eid, t);
    applyDamage(sim, t, dmg, eid);
    sim.emit({ type: "impact", pos: add(sim.posOf(t), { x: 0, y: sim.hero(t).height * 0.55, z: 0 }), kind: kick ? FX.Kick : FX.Fist });
    reduceGuard(sim, eid, secs(1.2));
    const combo = (hv(sim.c, eid, V.combo) + 1) % 5;
    setHv(sim.c, eid, V.combo, combo);
    if (!kick && combo === 4) knockUp(sim, t, 5);
  });
  return hit;
}

function startKick(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, yaw: number) {
  const d = viewDir(yaw, 0);
  startMove(sim, eid, KIND_KICK, secs(12 / 40), 40, { x: d.x * 40, y: 0.5, z: d.z * 40 });
}

const module: HeroModule = {
  def,
  kit: {
    init() {},
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      if (hv(c, eid, V.chi) > 0) setHv(c, eid, V.chi, hv(c, eid, V.chi) - 1);
      if (hv(c, eid, V.guard) > 0) {
        setHv(c, eid, V.guard, hv(c, eid, V.guard) - 1);
      }
      if (hv(c, eid, V.meditate) > 0) {
        setHv(c, eid, V.meditate, hv(c, eid, V.meditate) - 1);
        applyHeal(sim, eid, eid, 100 / 60, { noUlt: true });
        if (c.Health.hp[eid] >= c.Health.maxHp[eid] - 1) addOverhealth(sim, eid, 100, secs(2), eid);
        action(sim, eid, ActionKind.Cast, 3);
      }
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(12);
        setHv(c, eid, V.chi, EMPOWER_TICKS);
        applyStatus(sim, eid, "haste", 12, { hastePct: 0, source: eid });
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        setHv(c, eid, V.guard, secs(1.2));
        setHv(c, eid, V.chi, EMPOWER_TICKS);
        action(sim, eid, ActionKind.Cast, secs(1.2));
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        action(sim, eid, ActionKind.Cast, 10);
        startKick(sim, eid, input.yaw);
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        setHv(c, eid, V.meditate, secs(3));
        c.Vel.y[eid] = Math.max(0, c.Vel.y[eid]!);
        action(sim, eid, ActionKind.Cast, 30);
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
        c.Weapon.fireCd[eid] = empowered(sim, eid) ? secs(1 / 7) : secs(0.37);
        action(sim, eid, ActionKind.Melee, 7);
        strike(sim, eid, c.Ult.active[eid]! > 0 ? 6 : 3);
      }
    },
    movement(sim, eid, input, mods) {
      if (sim.c.Ult.active[eid]! > 0) mods.speedMul *= 1.2;
      const climbing = !!(input.buttons & Btn.Jump) && climb(sim, eid, input, mods, { speed: 9 });
      if (climbing) setHv(sim.c, eid, V.combo, 0);
      if (hv(sim.c, eid, V.meditate) > 0) {
        mods.speedMul *= 0.1;
        mods.gravityMul = 0;
        sim.c.Vel.y[eid] = 0;
      }
    },
    scripted(sim, eid, _input, kind) {
      if (kind === KIND_KICK && strike(sim, eid, 1.2, true)) sim.c.Move.ticks[eid] = 0;
    },
    onDamaged(sim, eid, amount, attacker) {
      if (hv(sim.c, eid, V.guard) <= 0 || attacker === undefined || sim.c.Player.team[attacker] === sim.c.Player.team[eid]) return amount;
      creditMitigation(sim, eid, amount);
      setHv(sim.c, eid, V.chi, Math.max(hv(sim.c, eid, V.chi), EMPOWER_TICKS));
      addOverhealth(sim, eid, Math.min(175, sim.c.Health.over[eid]! + amount * 1.3), secs(5), eid);
      sim.emit({ type: "impact", pos: add(sim.posOf(eid), { x: 0, y: sim.hero(eid).height * 0.6, z: 0 }), kind: FX.Chi });
      return 0;
    },
  },
  fx: {
    [FX.Fist]: { color: "#ffe680" },
    [FX.Kick]: { color: "#f2c14e" },
    [FX.Chi]: { color: "#7dffb0" },
  },
};

export default module;
